import styles from './mainHeaderItem.module.scss';
import {
  useDispatch,
  useSelector
} from "react-redux";
import { MethodType } from "../../../shared/components/methodType/methodType";
import {
  AppDispatch,
  RootState
} from "../../../store";
import { StatusImage } from "../../../shared/components/statusImage/statusImage";
import {
  TestCase,
  TestCaseTemplate,
  TransformTestCase
} from "../../../types/testCaseTemplateTypes";
import { TransformExecuteResponse } from "../../../types/clientTypes";
import loader from "../../../shared/images/loader-execute.gif";
import infoIcon from "../../../shared/images/info.svg";
import { TestCaseResult } from "../../../shared/components/testCaseResult/testCaseResult";
import { TestrailResult } from "../../../shared/components/testrailResult/testrailResult";
import {
  useCallback,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { getTransformTestCase } from "../../../utils/testCaseUtils";
import { ScenarioTestCase } from "../../../types/scenarioTypes";
import {
  setEditTestCaseId,
  updateScenarioExecuteResponse,
  updateScenarioSelectedTestCase
} from "../../../store/slices/scenarioSlice";
import { RunTestCasesButton } from "../../../components/runTestCasesButton/runTestCasesButton";
import editIcon from "../../../shared/images/edit.svg";
import { Popup } from "../../../shared/components/popup/popup";
import { TestCaseTemplateModal } from "../../../components/testCaseTemplateModal/testCaseTemplateModal";
import { useGetAllScenariosQuery } from "../../../api/scenarioService";
import { useGetAllTestCaseTemplateQuery } from "../../../api/testCaseTemplateService";
import { TestCaseModal } from "../../../components/testCaseModal/testCaseModal";

type MainHeaderItemProps = {
  testCaseTemplate: TestCaseTemplate;
  testCase: TestCase;
  executeResponse: TransformExecuteResponse;
  onClickRerun?: () => void;
  showEdit?: boolean;
  selected?: boolean;
  needSelect?: boolean;
  onClick?: () => void;
}

export const MainHeaderItem = ({ testCaseTemplate, testCase, showEdit, executeResponse, selected, needSelect, onClick, onClickRerun }: MainHeaderItemProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const statusCodes = useSelector((state: RootState) => state.mainSlice.statusCodes);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);
  const testCasesIsLaunched = useSelector((state: RootState) => state.mainSlice.testCasesIsLaunched);

  const selectedTestCases = useSelector((state: RootState) => state.scenarioSlice.selectedTestCases);

  const editContainer = useRef<HTMLDivElement>(null);

  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showEditTestCaseModal, setShowEditTestCaseModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);

  const { refetch: refetchScenarios } = useGetAllScenariosQuery("");
  const { refetch: refetchTemplates } = useGetAllTestCaseTemplateQuery("");

  const onClickEdit = useCallback(() => {
    setShowEditPopup(true);
  }, [setShowEditPopup]);

  const closeEditPopup = useCallback((): void => {
    setShowEditPopup(false);
  }, [setShowEditPopup]);

  const editTestCase = useCallback(() => {
    setShowEditTestCaseModal(true);
    closeEditPopup();
  }, [closeEditPopup, setShowEditTestCaseModal]);

  const editTemplate = useCallback(() => {
    setShowEditTemplateModal(true);
    closeEditPopup();
  }, [closeEditPopup, setShowEditTemplateModal]);

  const closeTestCaseTemplateModal = useCallback(() => {
    setShowEditTemplateModal(false);
  }, [setShowEditTemplateModal]);

  const updateTestCaseTemplate = useCallback(() => {
    dispatch(setEditTestCaseId(testCase.id));
    refetchTemplates();
    closeTestCaseTemplateModal();
  }, [testCase, closeTestCaseTemplateModal, refetchTemplates, dispatch]);

  const deleteTestCaseTemplate = useCallback(() => {
    refetchScenarios();
    refetchTemplates();
    closeTestCaseTemplateModal();
  }, [closeTestCaseTemplateModal, refetchScenarios, refetchTemplates]);

  const onCloseTestCaseModal = useCallback((isSave: boolean) => {
    if (isSave) {
      dispatch(setEditTestCaseId(testCase.id));
      refetchScenarios();
      refetchTemplates();
    }

    setShowEditTestCaseModal(false);
  }, [testCase, refetchScenarios, refetchTemplates, setShowEditTestCaseModal, dispatch]);

  const onClickRerunLocal = useCallback((): void => {
    const newSelectedTestCases: Array<ScenarioTestCase> = [];
    let newTransformTestCase: TransformTestCase | undefined;
    selectedTestCases.forEach(selectedTestCase => {
      if (selectedTestCase.internalId === testCase.internalId) {
        newTransformTestCase = getTransformTestCase(selectedVariables, testCaseTemplate, selectedTestCase);
        newSelectedTestCases.push({
          ...newTransformTestCase,
          queueNumber: selectedTestCase.queueNumber,
          isDeleted: selectedTestCase.isDeleted,
          isUpdated: selectedTestCase.isUpdated
        });
      } else {
        newSelectedTestCases.push(selectedTestCase);
      }
    });

    if (!!newTransformTestCase) {
      !!onClickRerun && onClickRerun();
      dispatch(updateScenarioSelectedTestCase(newSelectedTestCases));
    }
  }, [selectedTestCases, testCase, selectedVariables, testCaseTemplate, onClickRerun, dispatch]);

  const updateExecuteResponse = useCallback((executeResponse: TransformExecuteResponse) => {
    dispatch(updateScenarioExecuteResponse(executeResponse));
  }, [dispatch]);

  const containerClass: string = useMemo(() => {
    let result = styles.container;
    if (!!selected) {
      result += ` ${styles.selected}`;
    }
    if (!!needSelect) {
      result += ` ${styles.selectable}`;
    }
    return result;
  }, [selected, needSelect]);

  const statusCodeName = useMemo(() => {
    return statusCodes.find(item => item.code === executeResponse.statusCode)?.name;
  }, [statusCodes, executeResponse]);

  return (
    <>
      <div className={containerClass}
           onClick={onClick}>
        {!!testCaseTemplate?.id && !!testCase?.id && (
          <>
            <div className={styles.info}>
              <div className={testCaseTemplate.isPositive ? styles.positive : styles.negative}
                   title={testCaseTemplate.isPositive ? t("testCaseTemplate.positive") : t("testCaseTemplate.negative")}></div>
              <div>
                <MethodType methodId={testCaseTemplate.methodId}/>
              </div>
              <span title={testCase.name}>{testCase.name}</span>
              {!!testCaseTemplate.description && (<img src={infoIcon} title={testCaseTemplate.description} alt=""/>)}
              {showEdit && !testCasesIsLaunched && (
                <div className={styles.edit}
                     style={{display: showEditPopup ? "flex" : "none"}}
                     ref={editContainer}>
                  <img src={editIcon} alt="" onClick={onClickEdit}/>
                  <Popup isShow={showEditPopup}
                         classContainer={styles.popup}
                         left={-70}
                         top={5}
                         parent={editContainer.current}
                         onClose={closeEditPopup}
                         child={
                           <>
                             <div className={styles.item}
                                  onClick={editTestCase}>
                               {t("testCase.edit")}
                             </div>
                             <div className={styles.item}
                                  onClick={editTemplate}>
                               {t("testCaseTemplate.editTemplate")}
                             </div>
                           </>
                         }
                  />
                </div>
              )}
            </div>
            <div className={styles.rerunBlock}>
              <div className={`${styles.rerun} ${!needSelect ? styles.hide : ""}`}>
                <RunTestCasesButton selectedTestCases={[testCase]}
                                    clickRun={onClickRerunLocal}
                                    updateExecuteResponse={updateExecuteResponse}
                                    hideStopButton
                                    testCaseTemplates={[testCaseTemplate]}/>
              </div>
            </div>
            <div className={styles.status}>
              {executeResponse.statusCode > 0 && (
                <>
                  <StatusImage statusCodeId={executeResponse.statusCode} statusCode={executeResponse.statusCode}/>
                  {executeResponse.statusCode} {statusCodeName}
                </>
              )}
              {!!executeResponse.isLoad && (
                <img src={loader} alt=""/>
              )}
            </div>
            <TestCaseResult executeResponse={executeResponse}/>
            <div className={styles.timeContainer}>
              {!!executeResponse.time && (
                  <div className={styles.time}>
                    {executeResponse.time} {t("common.ms")}
                  </div>
              )}
            </div>
            <TestrailResult executeResponse={executeResponse}/>
          </>
        )}
      </div>

      { showEditTemplateModal && (
        <TestCaseTemplateModal onClose={closeTestCaseTemplateModal}
                               onCreate={closeTestCaseTemplateModal}
                               onDelete={deleteTestCaseTemplate}
                               onUpdate={updateTestCaseTemplate}
                               testCaseTemplate={testCaseTemplate} />
      )}

      { showEditTestCaseModal && (
        <TestCaseModal onClose={onCloseTestCaseModal}
                       testCase={testCase}
                       testCaseTemplate={testCaseTemplate}/>
      ) }

    </>
  )
}
